import React from "react";
import { Row, Col, Switch, Modal } from "antd";
import Input from "@dtci/spellbook/Input";
import Select from "@dtci/spellbook/Select";
import Tooltip from "@dtci/spellbook/Tooltip";
import Typography from "@dtci/spellbook/Typography";
import { InputLabel } from "tap/components/InputLabel";
import {
  SectionHeader,
  FormCard,
  ValidationError,
} from "../../components/shared";
import "./styles.scss";
import { usePermissions } from "tap/utils/permissions.js";

/**
 * Unified BasicInfoForm for both create and edit modes.
 * @param {Object} props
 * @param {Object} props.formData - The form data (rateCardForm or selectedRateCard)
 * @param {"create"|"edit"} props.mode - The mode of the form
 * @param {Function} props.handleInputChange - Handler for input changes
 * @param {Array} props.windowSequenceOptions - Window sequence options
 * @param {Array} props.currencyOptions - Currency options
 * @param {Array} props.priceTypeOptions - Price type options
 * @param {Array} props.revShareOptions - Rev share options
 * @param {Object} props.validationErrors - Validation errors object
 */
export default function BasicInfoForm({
  formData,
  mode = "create",
  handleInputChange,
  windowSequenceOptions,
  currencyOptions,
  priceTypeOptions,
  revShareOptions,
  validationErrors = {},
}) {
  const { isTapAdmin } = usePermissions();

  const isCreate = mode === "create";

  const getCurrentWindowSequence = () => {
    if (isCreate) {
      return formData?.windowSequence || "";
    }
    if (!Array.isArray(windowSequenceOptions)) return "";
    return (
      windowSequenceOptions.find(
        (opt) => opt.value === formData?.windowSequenceId,
      ) || ""
    );
  };

  const getCurrentCurrency = () => {
    if (isCreate) {
      return formData?.currency || "";
    }
    if (!Array.isArray(currencyOptions)) return "";
    return (
      currencyOptions.find(
        (opt) =>
          opt.label.replace(/\s*-\s*/g, " - ") ===
          (formData?.currency || "").replace(/\s*-\s*/g, " - "),
      ) || ""
    );
  };

  const getCurrentPriceType = () => {
    if (isCreate) {
      return formData?.priceType || "";
    }
    if (!Array.isArray(priceTypeOptions)) return "";
    return (
      priceTypeOptions.find((opt) => opt.label === formData?.priceType) || ""
    );
  };

  const getCurrentRevShareType = () => {
    if (isCreate) {
      return formData?.revShareType || "";
    }
    if (!Array.isArray(revShareOptions)) return "";
    return (
      revShareOptions.find((opt) => opt.label === formData?.revShareType) || ""
    );
  };

  const handleWindowSequenceChange = (value) => {
    const currentValue = isCreate
      ? formData?.windowSequence?.value
      : formData?.windowSequenceId;

    const newValue = isCreate ? value?.value : value?.data?.id;

    if (currentValue && value && newValue !== currentValue) {
      Modal.confirm({
        title: <Typography variant={"T40"}>Confirm Change</Typography>,
        content: (
          <div>
            <Typography variant={"T30"}>
              Are you sure you wish to change the windowing sequence?
            </Typography>
            <Typography variant={"T20"}>
              All windowing tiers for this rate card will be lost and will have
              to be reentered.
            </Typography>
          </div>
        ),
        okText: "Yes",
        cancelText: "No",
        maskClosable: false,
        centered: true,
        zIndex: 2000,
        onOk: () => handleInputChange("windowSequence", value),
      });
    } else {
      handleInputChange("windowSequence", value);
    }
  };

  const handlePriceTypeChange = (value) => {
    const currentValue = isCreate
      ? formData?.priceType?.value
      : formData?.priceType;

    const newValue = isCreate ? value?.value : value?.label;

    if (currentValue && value && newValue !== currentValue) {
      Modal.confirm({
        title: <Typography variant={"T40"}>Confirm Change</Typography>,
        content: (
          <div>
            <Typography variant={"T30"}>
              Are you sure you wish to change the Rate Card Price Type?
            </Typography>
            <Typography variant={"T20"}>
              All windowing tiers for this Rate Card will be lost and will have
              to be re-entered.
            </Typography>
          </div>
        ),
        okText: "Yes",
        cancelText: "No",
        maskClosable: false,
        centered: true,
        zIndex: 2000,
        onOk: () => handleInputChange("priceType", value),
      });
    } else {
      handleInputChange("priceType", value);
    }
  };

  return (
    <div>
      <SectionHeader title="Rate Card Details" />

      <FormCard>
        <Row gutter={16} align="middle" className="form-row">
          <Col span={12}>
            <InputLabel colProps={{ span: 24 }} label="Rate Card Name*">
              <Input
                value={formData?.rateCardName || ""}
                onChange={(e) =>
                  handleInputChange("rateCardName", e.target.value)
                }
                width="large"
                id="rateCardName"
                name="rateCardName"
                placeholder="Enter rate card name"
              />
              <ValidationError error={validationErrors.rateCardName} />
            </InputLabel>
          </Col>
          <Col span={12}>
            <div className="switch-row">
              <div className="switch-item">
                <Switch
                  checked={formData?.status !== "INACTIVE"}
                  size="default"
                  onChange={(value) =>
                    handleInputChange("status", value ? "ACTIVE" : "INACTIVE")
                  }
                  disabled={
                    isCreate ||
                    formData?.status === "NEEDS_ATTENTION" ||
                    formData?.status === "NEEDSATTENTION" ||
                    !isTapAdmin
                  }
                />
                <span className="switch-label">
                  {formData?.status || "ACTIVE"}
                </span>
              </div>
              <div className="switch-item">
                <Switch
                  checked={formData?.isTpr}
                  size="default"
                  onChange={(value) => handleInputChange("isTpr", value)}
                />
                <span className="switch-label">TPR Rate Card</span>
              </div>
            </div>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={6}>
            <InputLabel colProps={{ span: 24 }} label="Window Sequence*">
              <Tooltip
                tippyProps={{ placement: "right" }}
                tooltipContent={
                  formData?.windowSequence?.label || "Select a Window Sequence"
                }
              >
                <Select
                  placeholder="Select window sequence"
                  options={windowSequenceOptions}
                  isSearchable
                  onChange={handleWindowSequenceChange}
                  value={getCurrentWindowSequence()}
                />
              </Tooltip>
              <ValidationError error={validationErrors.windowSequence} />
            </InputLabel>
          </Col>
          <Col span={6}>
            <InputLabel colProps={{ span: 24 }} label="Currency*">
              <Tooltip
                tippyProps={{ placement: "right" }}
                tooltipContent={
                  formData?.currency?.label || "Select a Currency"
                }
              >
                <Select
                  placeholder="Select currency"
                  options={currencyOptions}
                  isSearchable
                  onChange={(value) => handleInputChange("currency", value)}
                  value={getCurrentCurrency()}
                />
              </Tooltip>
              <ValidationError error={validationErrors.currency} />
            </InputLabel>
          </Col>
          <Col span={6}>
            <InputLabel colProps={{ span: 24 }} label="Price Type*">
              <Select
                placeholder="Select price type"
                options={priceTypeOptions}
                isSearchable
                onChange={handlePriceTypeChange}
                value={getCurrentPriceType()}
              />
              <ValidationError error={validationErrors.priceType} />
            </InputLabel>
          </Col>
          <Col span={6}>
            <InputLabel colProps={{ span: 24 }} label="Rev Share Type*">
              <Select
                placeholder="Select rev share type"
                options={revShareOptions}
                isSearchable
                onChange={(value) => handleInputChange("revShareType", value)}
                value={getCurrentRevShareType()}
              />
              <ValidationError error={validationErrors.revShareType} />
            </InputLabel>
          </Col>
        </Row>
      </FormCard>
    </div>
  );
}
